import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import {
  alpha,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import I18n from "i18n";
import {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";

export type NestedSidebarItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  badge?: ReactNode;
  path?: string;
  exact?: boolean;
  visible?: boolean;
  children?: NestedSidebarItem[];
};

export interface NestedSidebarNavigationProps {
  items: NestedSidebarItem[];
  expanded: boolean;
  onRequestExpand?: () => void;
  onNavigate?: () => void;
  ariaLabel?: string;
}

function normalizePath(path: string) {
  if (path === "/") {
    return path;
  }
  return path.replace(/\/+$/, "");
}

function isLeafActive(
  pathname: string,
  item: Pick<NestedSidebarItem, "path" | "exact">,
) {
  if (!item.path) {
    return false;
  }

  const currentPath = normalizePath(pathname);
  const itemPath = normalizePath(item.path);
  return item.exact
    ? currentPath === itemPath
    : currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
}

function hasActiveDescendant(
  pathname: string,
  item: NestedSidebarItem,
): boolean {
  if (isLeafActive(pathname, item)) {
    return true;
  }
  return !!item.children?.some(child => hasActiveDescendant(pathname, child));
}

function getActiveParentIds(
  pathname: string,
  items: NestedSidebarItem[],
  parentIds: string[] = [],
): string[] {
  return items.reduce<string[]>((ids, item) => {
    if (!item.children?.length) {
      return ids;
    }

    const nextParentIds = [...parentIds, item.id];
    if (item.children.some(child => hasActiveDescendant(pathname, child))) {
      ids.push(...nextParentIds);
    }
    ids.push(...getActiveParentIds(pathname, item.children, nextParentIds));
    return ids;
  }, []);
}

function filterVisibleItems(items: NestedSidebarItem[]): NestedSidebarItem[] {
  return items.reduce<NestedSidebarItem[]>((visibleItems, item) => {
    if (item.visible === false) {
      return visibleItems;
    }

    const children = item.children
      ? filterVisibleItems(item.children)
      : undefined;
    if (item.children && !children?.length) {
      return visibleItems;
    }

    visibleItems.push({ ...item, children });
    return visibleItems;
  }, []);
}

export default function NestedSidebarNavigation({
  items,
  expanded,
  onRequestExpand,
  onNavigate,
  ariaLabel = I18n.t("Sidebar.mainNavigation"),
}: NestedSidebarNavigationProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { pathname } = useLocation();
  const visibleItems = useMemo(() => filterVisibleItems(items), [items]);
  const activeParentIds = useMemo(
    () => new Set(getActiveParentIds(pathname, visibleItems)),
    [pathname, visibleItems],
  );
  const activeParentKey = Array.from(activeParentIds).sort().join("|");
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [tooltipItemId, setTooltipItemId] = useState<string>();
  const [compactMenu, setCompactMenu] = useState<{
    anchorPosition: { top: number; left: number };
    item: NestedSidebarItem;
  }>();

  useEffect(() => {
    setOpenItems(current => {
      const next = { ...current };
      activeParentIds.forEach(id => {
        next[id] = true;
      });
      return next;
    });
  }, [activeParentKey]);

  useEffect(() => {
    if (expanded || !isDesktop) {
      setTooltipItemId(undefined);
      setCompactMenu(undefined);
    }
  }, [expanded, isDesktop]);

  const toggleItem = (
    event: ReactMouseEvent<HTMLElement>,
    item: NestedSidebarItem,
  ) => {
    setTooltipItemId(undefined);
    if (!expanded) {
      const anchorRect = event.currentTarget.getBoundingClientRect();
      setCompactMenu(current =>
        current?.item.id === item.id
          ? undefined
          : {
              anchorPosition: {
                top: anchorRect.top,
                left: anchorRect.right + 8,
              },
              item,
            },
      );
      return;
    }
    setOpenItems(current => ({ ...current, [item.id]: !current[item.id] }));
  };

  const handleNavigate = () => {
    setCompactMenu(undefined);
    onNavigate?.();
  };

  const renderFlyoutItem = (
    item: NestedSidebarItem,
    depth: number,
  ): ReactNode => {
    const hasChildren = !!item.children?.length;
    const active = hasActiveDescendant(pathname, item);
    const open = activeParentIds.has(item.id) || !!openItems[item.id];

    return (
      <Box component="li" key={item.id} sx={{ display: "block" }}>
        <ListItemButton
          component={!hasChildren && item.path ? Link : "button"}
          to={!hasChildren && item.path ? item.path : undefined}
          selected={!hasChildren && active}
          aria-current={!hasChildren && active ? "page" : undefined}
          aria-expanded={hasChildren ? open : undefined}
          onClick={
            hasChildren
              ? () =>
                  setOpenItems(current => ({
                    ...current,
                    [item.id]: !current[item.id],
                  }))
              : handleNavigate
          }
          sx={{
            minHeight: 42,
            mx: 0.75,
            my: 0.25,
            px: 1.25,
            pl: 1.25 + depth * 1.5,
            borderRadius: 1.5,
            "&.Mui-selected": {
              backgroundColor: theme => alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
              "&:hover": {
                backgroundColor: theme =>
                  alpha(theme.palette.primary.main, 0.18),
              },
            },
          }}
        >
          {!!item.icon && (
            <ListItemIcon
              sx={{ minWidth: 34, color: active ? "primary.main" : "inherit" }}
            >
              {item.icon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: depth === 0 ? "0.875rem" : "0.825rem",
              fontWeight: active ? 700 : 500,
            }}
          />
          {item.badge}
          {hasChildren && (
            <ChevronRightRoundedIcon
              fontSize="small"
              sx={{
                ml: 0.5,
                transform: open ? "rotate(90deg)" : "none",
                transition: theme => theme.transitions.create("transform"),
              }}
            />
          )}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={open} timeout={160} unmountOnExit>
            <List component="ul" disablePadding>
              {item.children!.map(child => renderFlyoutItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  const renderItem = (item: NestedSidebarItem, depth: number): ReactNode => {
    const hasChildren = !!item.children?.length;
    const active = hasActiveDescendant(pathname, item);
    const open =
      expanded && (activeParentIds.has(item.id) || !!openItems[item.id]);
    const content = (
      <>
        {!!item.icon && (
          <ListItemIcon
            sx={{
              minWidth: expanded ? 36 : 0,
              color: "inherit",
              justifyContent: "center",
            }}
          >
            {item.icon}
          </ListItemIcon>
        )}
        <ListItemText
          primary={item.label}
          sx={{
            display: expanded ? "block" : "none",
            my: 0,
            minWidth: 0,
            "& .MuiListItemText-primary": {
              fontSize: depth === 0 ? "0.875rem" : "0.825rem",
              fontWeight: active ? 700 : depth === 0 ? 600 : 500,
              whiteSpace: "normal",
              lineHeight: 1.25,
            },
          }}
        />
        {expanded && item.badge}
        {expanded && hasChildren && (
          <ChevronRightRoundedIcon
            fontSize="small"
            sx={{
              ml: 0.5,
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              transition: theme =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
          />
        )}
      </>
    );

    const button = (
      <ListItemButton
        component={!hasChildren && item.path ? Link : "button"}
        to={!hasChildren && item.path ? item.path : undefined}
        selected={!hasChildren && active}
        aria-current={!hasChildren && active ? "page" : undefined}
        aria-expanded={hasChildren ? open : undefined}
        aria-controls={hasChildren ? `sidebar-group-${item.id}` : undefined}
        aria-label={!expanded ? item.label : undefined}
        onClick={
          hasChildren
            ? (event: ReactMouseEvent<HTMLElement>) => toggleItem(event, item)
            : handleNavigate
        }
        onMouseEnter={() => {
          if (!expanded && isDesktop) {
            setTooltipItemId(item.id);
          }
        }}
        onMouseLeave={() => {
          setTooltipItemId(current =>
            current === item.id ? undefined : current,
          );
        }}
        sx={{
          minHeight: 44,
          width: expanded ? "auto" : 48,
          mx: expanded ? 1 : "auto",
          my: 0.25,
          px: expanded ? 1.5 : 0,
          pl: expanded ? 1.5 + depth * 1.5 : 0,
          borderRadius: 1.5,
          justifyContent: expanded ? "flex-start" : "center",
          position: "relative",
          color: theme =>
            alpha(
              theme.palette.getContrastText(theme.palette.primary.main),
              0.8,
            ),
          "&:hover": {
            color: "common.white",
            backgroundColor: theme => alpha(theme.palette.common.white, 0.1),
          },
          "&.Mui-selected": {
            color: "common.white",
            backgroundColor: theme => alpha(theme.palette.common.white, 0.18),
            "&:hover": {
              backgroundColor: theme => alpha(theme.palette.common.white, 0.24),
            },
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 8,
              bottom: 8,
              width: 3,
              borderRadius: "0 3px 3px 0",
              backgroundColor: "common.white",
            },
          },
          ...(!expanded && hasChildren
            ? {
                "&::after": {
                  content: '""',
                  position: "absolute",
                  right: 5,
                  bottom: 6,
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: active
                    ? "common.white"
                    : alpha(theme.palette.common.white, 0.48),
                  boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
              }
            : {}),
        }}
      >
        {content}
      </ListItemButton>
    );

    return (
      <Box component="li" key={item.id} sx={{ display: "block" }}>
        <Tooltip
          title={item.label}
          placement="right"
          open={
            !expanded &&
            isDesktop &&
            tooltipItemId === item.id &&
            compactMenu?.item.id !== item.id
          }
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          {button}
        </Tooltip>
        {hasChildren && (
          <Collapse in={open} timeout={180} unmountOnExit>
            <List
              id={`sidebar-group-${item.id}`}
              component="ul"
              disablePadding
              aria-label={item.label}
            >
              {item.children!.map(child => renderItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box component="nav" aria-label={ariaLabel} sx={{ width: "100%" }}>
        <List component="ul" disablePadding>
          {visibleItems.map(item => renderItem(item, 0))}
        </List>
      </Box>
      <Popover
        open={!expanded && isDesktop && !!compactMenu}
        anchorReference="anchorPosition"
        anchorPosition={compactMenu?.anchorPosition}
        onClose={() => setCompactMenu(undefined)}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 290,
              maxHeight: "min(520px, calc(100vh - 24px))",
              borderRadius: 2,
              overflow: "hidden",
              border: theme => `1px solid ${theme.palette.divider}`,
              boxShadow: theme => theme.shadows[12],
            },
          },
        }}
      >
        {compactMenu && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minHeight: 50,
                px: 2,
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
                backgroundColor: theme =>
                  alpha(theme.palette.primary.main, 0.06),
              }}
            >
              <Box sx={{ display: "flex", color: "primary.main" }}>
                {compactMenu.item.icon}
              </Box>
              <ListItemText
                primary={compactMenu.item.label}
                primaryTypographyProps={{ fontWeight: 700 }}
              />
            </Box>
            <List
              component="ul"
              disablePadding
              sx={{ py: 0.75, overflowY: "auto", maxHeight: "inherit" }}
            >
              {compactMenu.item.children?.map(item =>
                renderFlyoutItem(item, 0),
              )}
            </List>
          </>
        )}
      </Popover>
    </>
  );
}
